import { css } from "@tento-ui/component-styles";
import {
  CodexTextBlockPlugin,
  CodexToolCallBlockPlugin,
} from "./codex-blocks";
import { ChatCodexTurn } from "./codex-turn";
import { ChatThemes } from "../chat-theme";
import type { ChatTheme } from "../chat-theme";

/**
 * The Codex CLI look, from the TUI's own source (tui/src/style.rs): user
 * turns are a full-width band of 12% white over the terminal ground — no
 * bubble, no gutter; agent/quote markers are dim, the accent is bold cyan,
 * green/red carry status. Ground here is a canonical dark terminal.
 *
 * The capture's SGR-yellow markers retint through the SGR name hooks (the
 * tsh role vocabulary authors its accent as yellow and node refs as cyan) —
 * one palette line restyles the whole session. The look never pads the
 * thread's :host: each row insets itself through --chat-turn-inset, which is
 * how the user band bleeds full-width while its text keeps the gutter.
 */
export const CodexLook: ChatTheme = {
  ...ChatThemes.tui(),
  name: "codex",
  makeTurn: () => ChatCodexTurn.make() as ChatCodexTurn,
  // The dialect this look speaks, carried by the look rather than baked into
  // the register. `ChatCodexTurn.pluginTypeFor` routes blocks onto these two
  // types; nothing else in the repo asks for them, so nothing else has to
  // know they exist.
  plugins: () => [new CodexToolCallBlockPlugin(), new CodexTextBlockPlugin()],
  $style: css`
    :host {
      background: #161616;
      color: #e8e8e8;
      --text-pri: #e8e8e8;
      --text-sec: #9a9a9a;
      --text-ter: #6f6f6f;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 12px;
      line-height: round(nearest, 1.6em, 1px);
      --thread-top-inset: 12px;
      --thread-bottom-inset: 12px;
      --chat-turn-inset: 16px;
      --chat-terminal-font: ui-monospace, SFMono-Regular, Menlo, monospace;
      --chat-terminal-size: 12px;
      --chat-terminal-leading: round(nearest, 1.6em, 1px);
      --session-replay-ansi-ground: #161616;
      /* codex marks turns with DIM prefixes, not colour — the capture's
         SGR-yellow markers stand down to gray, and cyan is the accent. */
      --session-replay-ansi-yellow: #8f8f8f;
      --session-replay-ansi-cyan: #39c5cf;
      /* The CLI breathes in blank lines: one between a turn's items, one
         after the turn, and one above and below the user's text INSIDE
         the band. */
      --chat-tui-block-gap: 1lh;
      --chat-tui-turn-gap: 1lh;
      --chat-tui-prompt-pad: 1lh;
    }

    /* The user band, per codex tui/src/style.rs user_message_bg_rgb:
       12% white over the terminal ground, full width — but stopping
       above the turn gap, so the blank line after the band stays the
       terminal's ground, exactly as the CLI paints it. */
    std-chat-codex-turn[user] {
      background: linear-gradient(
            color-mix(in srgb, #ffffff 12%, #161616),
            color-mix(in srgb, #ffffff 12%, #161616)
          )
          top / 100% calc(100% - var(--chat-tui-turn-gap, 0px)) no-repeat;
    }
  `,
};
